// src/components/AddChallengeForm.js

import React, { useState } from 'react';
import axios from 'axios';
import { Container, Form, Button, Row, Col, Alert, Spinner, InputGroup } from 'react-bootstrap';
import styled from 'styled-components';
import ENDPOINT from '../../context/EndPoint';

// --- Styled Components for Custom UI Elements ---

const FormWrapper = styled.div`
  background: #ffffff;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  margin-top: 50px;
  margin-bottom: 50px;
`;

const SectionTitle = styled.h4`
  color: #333;
  border-left: 4px solid #0d6efd;
  padding-left: 12px;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
`;

const DynamicInputWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const SkillBadge = styled.span`
  background-color: #e9ecef;
  color: #495057;
  padding: 5px 10px;
  border-radius: 15px;
  margin: 0 5px 5px 0;
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;

  button {
    background: none;
    border: none;
    color: #6c757d;
    margin-left: 8px;
    font-weight: bold;
    padding: 0;
    line-height: 1;

    &:hover {
      color: #dc3545;
    }
  }
`;

// --- The Main Form Component ---

const AddHackingChallenge = () => {
    // State for simple form fields
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [prizeMoney, setPrizeMoney] = useState('');
    const [deadline, setDeadline] = useState('');

    // State for dynamic list fields
    const [skills, setSkills] = useState([]);
    const [currentSkill, setCurrentSkill] = useState('');
    const [instructions, setInstructions] = useState(['']);
    const [submissionProcess, setSubmissionProcess] = useState(['']);

    // State for handling submission status (loading, error, success)
    const [status, setStatus] = useState({ loading: false, error: null, success: null });

    // --- Handlers for dynamic lists ---

    const handleDynamicChange = (index, value, field) => {
        if (field === 'instructions') {
            const newInstructions = [...instructions];
            newInstructions[index] = value;
            setInstructions(newInstructions);
        } else if (field === 'submission') {
            const newProcess = [...submissionProcess];
            newProcess[index] = value;
            setSubmissionProcess(newProcess);
        }
    };

    const addDynamicField = (field) => {
        if (field === 'instructions') {
            setInstructions([...instructions, '']);
        } else if (field === 'submission') {
            setSubmissionProcess([...submissionProcess, '']);
        }
    };

    const removeDynamicField = (index, field) => {
        if (field === 'instructions') {
            setInstructions(instructions.filter((_, i) => i !== index));
        } else if (field === 'submission') {
            setSubmissionProcess(submissionProcess.filter((_, i) => i !== index));
        }
    };

    const handleAddSkill = () => {
        if (currentSkill && !skills.includes(currentSkill)) {
            setSkills([...skills, currentSkill]);
            setCurrentSkill('');
        }
    };

    const handleRemoveSkill = (skillToRemove) => {
        setSkills(skills.filter(skill => skill !== skillToRemove));
    };

    // --- Form Submission Handler ---

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, error: null, success: null });

        // FormData is the best way to send data that PHP's $_POST can easily read.
        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('prize_money', prizeMoney);
        formData.append('submission_deadline', deadline);

        // For arrays, PHP expects keys like 'skills_required[]'
        skills.forEach(skill => formData.append('skills_required[]', skill));
        instructions.forEach(inst => formData.append('instructions[]', inst));
        submissionProcess.forEach(step => formData.append('submission_process[]', step));

        try {
            const response = await axios.post(
                ENDPOINT + 'add-hacking-challenge.php', 
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response.data.status === 'success') {
                setStatus({ loading: false, success: 'Challenge added successfully!', error: null });
                // Optionally reset the form here
            } else {
                setStatus({ loading: false, error: response.data.message || 'An unknown error occurred.', success: null });
            }
        } catch (error) {
            setStatus({ loading: false, error: 'Failed to connect to the server. Please try again.', success: null });
        }
    };

    return (
        <Container>
            <Row className="justify-content-center">
                <Col md={10} lg={8}>
                    <FormWrapper>
                        <h2 className="text-center mb-4">Create a New Hacking Challenge</h2>
                        <Form onSubmit={handleSubmit}>
                            {/* --- Basic Information --- */}
                            <SectionTitle>Basic Information</SectionTitle>
                            <Form.Group className="mb-3" controlId="title">
                                <Form.Label>Challenge Title</Form.Label>
                                <Form.Control type="text" placeholder="e.g., Secure the Server" value={title} onChange={(e) => setTitle(e.target.value)} required />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="description">
                                <Form.Label>Description</Form.Label>
                                <Form.Control as="textarea" rows={3} placeholder="Briefly describe the challenge goal." value={description} onChange={(e) => setDescription(e.target.value)} required />
                            </Form.Group>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="prizeMoney">
                                        <Form.Label>Prize Money ($)</Form.Label>
                                        <Form.Control type="number" placeholder="e.g., 500" value={prizeMoney} onChange={(e) => setPrizeMoney(e.target.value)} required />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="deadline">
                                        <Form.Label>Submission Deadline</Form.Label>
                                        <Form.Control type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} required />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* --- Skills Required --- */}
                            <SectionTitle>Skills Required</SectionTitle>
                            <InputGroup className="mb-3">
                                <Form.Control
                                    placeholder="Add a skill (e.g., SQL Injection)"
                                    value={currentSkill}
                                    onChange={(e) => setCurrentSkill(e.target.value)}
                                    onKeyPress={(e) => { if(e.key === 'Enter') { e.preventDefault(); handleAddSkill(); }}}
                                />
                                <Button variant="outline-primary" onClick={handleAddSkill}>Add</Button>
                            </InputGroup>
                            <div>
                                {skills.map((skill, index) => (
                                    <SkillBadge key={index}>
                                        {skill}
                                        <button onClick={() => handleRemoveSkill(skill)}>×</button>
                                    </SkillBadge>
                                ))}
                            </div>

                            {/* --- Instructions --- */}
                            <SectionTitle>Instructions</SectionTitle>
                            {instructions.map((inst, index) => (
                                <DynamicInputWrapper key={index}>
                                    <Form.Control as="textarea" rows={2} placeholder={`Step ${index + 1}`} value={inst} onChange={(e) => handleDynamicChange(index, e.target.value, 'instructions')} required />
                                    {instructions.length > 1 && (
                                        <Button variant="outline-danger" className="ms-2" onClick={() => removeDynamicField(index, 'instructions')}>-</Button>
                                    )}
                                </DynamicInputWrapper>
                            ))}
                            <Button variant="link" onClick={() => addDynamicField('instructions')}>+ Add Instruction</Button>

                            {/* --- Submission Process --- */}
                            <SectionTitle>Submission Process</SectionTitle>
                            {submissionProcess.map((step, index) => (
                                <DynamicInputWrapper key={index}>
                                    <Form.Control as="textarea" rows={2} placeholder={`Step ${index + 1}`} value={step} onChange={(e) => handleDynamicChange(index, e.target.value, 'submission')} required />
                                    {submissionProcess.length > 1 && (
                                        <Button variant="outline-danger" className="ms-2" onClick={() => removeDynamicField(index, 'submission')}>-</Button>
                                    )}
                                </DynamicInputWrapper>
                            ))}
                            <Button variant="link" onClick={() => addDynamicField('submission')}>+ Add Submission Step</Button>

                            {/* --- Submission & Status --- */}
                            <hr className="my-4" />
                            {status.error && <Alert variant="danger">{status.error}</Alert>}
                            {status.success && <Alert variant="success">{status.success}</Alert>}
                            <div className="d-grid">
                                <Button variant="primary" type="submit" size="lg" disabled={status.loading}>
                                    {status.loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Create Challenge'}
                                </Button>
                            </div>
                        </Form>
                    </FormWrapper>
                </Col>
            </Row>
        </Container>
    );
};

export default AddHackingChallenge;